import { IPromoDeductionMatchSummary, IPromoSystemFlags } from "@/core/repositories/promotion";

export const formatConfidence = (score: number): number => Math.round(score);

export const getMatchStatusChipColor = (status: string): "success" | "secondary" | "warning" | "grey" => {
  if (status === "AUTO_MATCHED") {
    return "success";
  }
  if (status === "NEEDS_REVIEW" || status === "PARTIAL_MATCH") {
    return "secondary";
  }
  return "warning";
};

export const formatMatchStatusLabel = (status: string): string => {
  if (status.includes("_")) {
    return status
      .split("_")
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  }

  return status
    .split(" ")
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

export interface PromoCriterionRow {
  label: string;
  value: string;
  aligned: boolean;
}

export const formatPromoDateLabel = (isoDate: string): string => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatPromoDateWindow = (startDate: string, endDate: string): string => {
  const startYear = new Date(startDate).getFullYear().toString().slice(-2);
  return `Within ${formatPromoDateLabel(startDate)} - ${formatPromoDateLabel(endDate)}, '${startYear} window`;
};

const formatSkuCoverageValue = (
  skuFullyCovered: boolean,
  coverageRatio: number | null,
  matchedItems: number,
  totalItems: number
): string => {
  const coveragePercent =
    coverageRatio != null
      ? Math.round(coverageRatio * 100)
      : totalItems > 0
        ? Math.round((matchedItems / totalItems) * 100)
        : 0;

  if (skuFullyCovered) {
    return `${coveragePercent}% covered`;
  }

  return `${coveragePercent}% covered` + (totalItems > 0 ? ` (${matchedItems}/${totalItems} items)` : "");
};

const formatAmountValue = (allowanceAligned: boolean, discountAligned: boolean): string => {
  if (allowanceAligned && discountAligned) {
    return "Within tolerance";
  }
  if (!allowanceAligned && !discountAligned) {
    return "Allowance and discount mismatch";
  }
  return allowanceAligned ? "Discount mismatch" : "Allowance mismatch";
};

export const matchCriteriaToDisplayRows = (match: IPromoDeductionMatchSummary): PromoCriterionRow[] => {
  const { criteriaAlignment, promotion, scoringPhases } = match;
  const { phase4 } = scoringPhases;

  return [
    {
      label: "Purchaser",
      value: promotion?.purchaserName?.trim() || "Unknown",
      aligned: criteriaAlignment?.purchaserAligned,
    },
    {
      label: "Retailer",
      value: promotion?.retailerName?.trim() || "Unknown",
      aligned: criteriaAlignment?.retailerAligned,
    },
    {
      label: "Period",
      value: formatPromoDateWindow(promotion?.promoStartDate, promotion?.promoEndDate),
      aligned: criteriaAlignment?.dateInWindow,
    },
    {
      label: "Reason Code",
      value: promotion?.type?.promoTypeCode || "Unknown",
      aligned: criteriaAlignment?.typeAligned,
    },
    {
      label: "Amount",
      value: formatAmountValue(criteriaAlignment?.allowanceAligned, criteriaAlignment?.discountAligned),
      aligned: criteriaAlignment.allowanceAligned && criteriaAlignment.discountAligned,
    },
    {
      label: "SKU Coverage",
      value: formatSkuCoverageValue(
        criteriaAlignment?.skuFullyCovered,
        phase4.coverageRatio,
        phase4.matchedItems,
        phase4.totalItems
      ),
      aligned: criteriaAlignment?.skuFullyCovered,
    },
  ];
};

const SYSTEM_FLAG_LABELS: Record<keyof IPromoSystemFlags, string> = {
  isDuplicateFlag: "Duplicate",
  isBalanceInsufficientFlag: "Insufficient balance",
  isLowCoverageFlag: "Low coverage",
  isHeaderOnlyMatch: "Header only",
};

export const getActiveSystemFlags = (systemFlags: IPromoSystemFlags): string[] =>
  (Object.keys(SYSTEM_FLAG_LABELS) as (keyof IPromoSystemFlags)[])
    .filter(key => systemFlags[key])
    .map(key => SYSTEM_FLAG_LABELS[key]);

const AUTO_APPLICATION_SOURCES = ["AUTO", "SYSTEM", "AUTO_MATCH"];

export const getAppliedMatch = (matches: IPromoDeductionMatchSummary[]): IPromoDeductionMatchSummary | null =>
  matches.find(match => Boolean(match.matchSummary.appliedAt)) ?? null;

export const isAutoAppliedMatch = (match: IPromoDeductionMatchSummary): boolean => {
  if (!match.matchSummary.appliedAt) {
    return false;
  }
  const source = match.matchSummary.applicationSource?.trim().toUpperCase() ?? "";
  return AUTO_APPLICATION_SOURCES.some(autoSource => source.includes(autoSource));
};

export const shouldShowApproveApplyCTA = ({
  matches,
  primaryMatch,
  canEdit,
  canSelect,
  isApplying,
}: {
  matches: IPromoDeductionMatchSummary[];
  primaryMatch: IPromoDeductionMatchSummary | null;
  canEdit: boolean;
  canSelect: boolean;
  isApplying: boolean;
}): boolean => {
  if (!canEdit || !canSelect || isApplying || !primaryMatch) {
    return false;
  }

  const applied = getAppliedMatch(matches);

  if (applied && primaryMatch.matchResultId === applied.matchResultId && isAutoAppliedMatch(primaryMatch)) {
    return false;
  }

  if (!primaryMatch.matchSummary.appliedAt) {
    return true;
  }

  if (applied && primaryMatch.matchResultId !== applied.matchResultId) {
    return true;
  }

  return false;
};

export const getMatchStatusChipLabel = (match: IPromoDeductionMatchSummary): string => {
  return formatMatchStatusLabel(match.matchSummary.displayMatchStatus);
};

export const getMatchStatusChipColorForMatch = (
  match: IPromoDeductionMatchSummary
): "success" | "secondary" | "warning" | "grey" => {
  if (match.matchSummary.appliedAt) {
    return "success";
  }
  return getMatchStatusChipColor(match.matchSummary.matchStatus);
};

export const resolveInitialSelectedMatchId = (matches: IPromoDeductionMatchSummary[]): number | null => {
  const applied = getAppliedMatch(matches);
  if (applied) {
    return applied.matchResultId;
  }
  const { bestMatch } = splitBestAndAlternativeMatches(matches);
  return bestMatch?.matchResultId ?? null;
};

export const splitBestAndAlternativeMatches = (
  matches: IPromoDeductionMatchSummary[]
): { bestMatch: IPromoDeductionMatchSummary | null; alternatives: IPromoDeductionMatchSummary[] } => {
  if (matches.length === 0) {
    return { bestMatch: null, alternatives: [] };
  }

  const bestIndex = matches.findIndex(match => match.matchSummary.isBestMatch);
  const resolvedBestIndex = bestIndex >= 0 ? bestIndex : 0;

  return {
    bestMatch: matches[resolvedBestIndex],
    alternatives: matches.filter((_, index) => index !== resolvedBestIndex),
  };
};

export const getOutcomeChipColor = (outcome: string): "success" | "warning" | "error" | "grey" => {
  if (outcome === "PASS") {
    return "success";
  }
  if (outcome === "FAIL") {
    return "error";
  }
  return "warning";
};
